import { extensionManager } from '../../../App';
import type { PeerReview } from '../../services/peerReviewService';
import { getPeerReviewStudyFields } from '../../services/peerReviewService';

type QidoStudyHit = {
  studyInstanceUid?: string;
  accession?: string;
  mrn?: string;
  modalities?: string;
};

type StudiesSearch = (params: Record<string, unknown>) => Promise<QidoStudyHit[]>;

export type ResolvedPeerReviewViewerStudy = {
  studyInstanceUid: string;
  resolvedVia: string;
  modalities?: string;
};

/**
 * Peer Review sits outside DataSourceWrapper, so the active DICOMweb source may
 * never have been initialized (qido client created in initialize()). Mirror the
 * worklist/Mode path before any studies.search call.
 */
async function getReadyStudiesSearch(): Promise<StudiesSearch | null> {
  const dataSources = extensionManager?.getActiveDataSource?.();
  const dataSource = Array.isArray(dataSources) ? dataSources[0] : dataSources;
  if (!dataSource) {
    return null;
  }

  const initialize = (
    dataSource as {
      initialize?: (args: {
        params: Record<string, unknown>;
        query: URLSearchParams;
      }) => Promise<void> | void;
    }
  ).initialize;

  if (typeof initialize === 'function') {
    try {
      await initialize({ params: {}, query: new URLSearchParams() });
    } catch (error) {
      console.warn('Peer review: data source initialize failed before QIDO resolve', error);
      return null;
    }
  }

  const studies = (
    dataSource as {
      query?: { studies?: { search?: StudiesSearch } };
    }
  )?.query?.studies;

  const search = studies?.search;
  if (typeof search !== 'function' || !studies) {
    return null;
  }

  return search.bind(studies) as StudiesSearch;
}

function pickHitUid(hit?: QidoStudyHit | null): string {
  return hit?.studyInstanceUid?.trim() || '';
}

function toResolvedHit(
  hit: QidoStudyHit | null | undefined,
  resolvedVia: string
): ResolvedPeerReviewViewerStudy | null {
  const uid = pickHitUid(hit);
  if (!uid) {
    return null;
  }
  return {
    studyInstanceUid: uid,
    resolvedVia,
    modalities: hit?.modalities || undefined,
  };
}

async function resolveByStudyUid(
  search: StudiesSearch,
  risUid: string
): Promise<ResolvedPeerReviewViewerStudy | null> {
  try {
    const byUid = await search({ studyInstanceUid: risUid, limit: 1 });
    return toResolvedHit(byUid?.[0], 'study-instance-uid');
  } catch (error) {
    console.warn('Peer review QIDO UID lookup failed:', error);
    return null;
  }
}

async function resolveByAccession(
  search: StudiesSearch,
  accession: string
): Promise<ResolvedPeerReviewViewerStudy | null> {
  try {
    const byAccession = await search({
      accessionNumber: accession,
      disableWildcard: true,
      limit: 25,
    });
    const exact =
      byAccession?.find(
        study => (study.accession || '').trim().toUpperCase() === accession.toUpperCase()
      ) || byAccession?.[0];
    return toResolvedHit(exact, 'accession-number');
  } catch (error) {
    console.warn('Peer review QIDO accession lookup failed:', error);
    return null;
  }
}

function resolveFromPatientHits(
  byPatient: QidoStudyHit[] | undefined,
  risUid: string,
  accession: string
): ResolvedPeerReviewViewerStudy | null {
  if (risUid) {
    const matchUid = byPatient?.find(study => study.studyInstanceUid === risUid);
    const resolved = toResolvedHit(matchUid, 'patient-id+uid');
    if (resolved) {
      return resolved;
    }
  }
  if (accession) {
    const matchAcc = byPatient?.find(
      study => (study.accession || '').trim().toUpperCase() === accession.toUpperCase()
    );
    const resolved = toResolvedHit(matchAcc, 'patient-id+accession');
    if (resolved) {
      return resolved;
    }
  }
  if (byPatient?.length === 1) {
    return toResolvedHit(byPatient[0], 'patient-id');
  }
  return null;
}

async function resolveByPatientId(
  search: StudiesSearch,
  patientId: string,
  risUid: string,
  accession: string
): Promise<ResolvedPeerReviewViewerStudy | null> {
  try {
    const byPatient = await search({
      patientId,
      disableWildcard: true,
      limit: 50,
    });
    return resolveFromPatientHits(byPatient, risUid, accession);
  } catch (error) {
    console.warn('Peer review QIDO patient lookup failed:', error);
    return null;
  }
}

/**
 * Resolve a PACS-visible StudyInstanceUID before opening the viewer — same
 * QIDO studies.search the Studies worklist / PanelStudyBrowser use.
 * Only returns UIDs confirmed by QIDO (never unverified RIS fallbacks).
 */
export async function resolvePeerReviewViewerStudyUid(
  row: PeerReview
): Promise<ResolvedPeerReviewViewerStudy | null> {
  const fields = getPeerReviewStudyFields(row);
  const risUid = row.study_instance_uid?.trim() || fields.study_instance_uid?.trim() || '';
  const accession = fields.accession_number?.trim() || '';
  const patientId = fields.patient_uhid?.trim() || '';
  const search = await getReadyStudiesSearch();

  if (!search) {
    console.warn('Peer review viewer: active data source / studies.search unavailable');
    return null;
  }

  if (risUid) {
    const resolved = await resolveByStudyUid(search, risUid);
    if (resolved) {
      return resolved;
    }
  }

  if (accession) {
    const resolved = await resolveByAccession(search, accession);
    if (resolved) {
      return resolved;
    }
  }

  if (patientId) {
    return resolveByPatientId(search, patientId, risUid, accession);
  }

  return null;
}
